import { intelligenceThreads } from '@cwos/api-client';
import type { IntelligenceThread, ThreadsClient } from './threads.js';

/**
 * THE ONE BINDING SITE for the durable thread API.
 *
 * The sidebar codes against the {@link ThreadsClient} port; this file decides
 * what implements it, and every shape difference between the port and the wire
 * is reconciled HERE so the pane keeps one vocabulary.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * WHAT THIS FILE USED TO SAY, AND WHY IT IS WORTH RECORDING.
 *
 * It returned `undefined` and explained, at length, that nothing could
 * implement the port: that `packages/api-client/src` had no
 * `intelligenceThreads` module and that the API mounted "exactly two
 * intelligence doors" over an in-process `Map` with a TTL. Both sentences were
 * true when they were written. Both were false for months afterwards — the
 * module ships, the migration ships, and `server.ts` registers six thread
 * routes — and nothing noticed, because a docblock cannot observe its own
 * expiry. The whole domain was separated from working by one function body,
 * and the function body was guarded by a paragraph asserting that it had to be.
 *
 * The two stand-ins it refused are still refused, and its reasoning still
 * holds: `localStorage` would make history a property of one browser, and a
 * client that rejects would render an error where a capability simply was not
 * switched on. Neither is what this returns now. This returns the server.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * THE ADAPTATION, field by field, so the pane never sees a wire shape.
 *
 *   `id`             → `threadId`
 *   `pinnedAt`       → `pinned` (an ISO instant when pinned, null when not)
 *   `lastActivityAt` → `updatedAtMs`
 *
 * `lastActivityAt` and not `updatedAt`, deliberately: the server moves
 * `lastActivityAt` only when something was SAID, so renaming or pinning an old
 * conversation does not haul it back into "Today". The date grouping reads
 * what the person would call activity.
 *
 * Both mutations resolve to the server's own row rather than to void, which is
 * what lets the pane reconcile from the response instead of from what it hoped
 * would happen.
 */
function toThread(row: intelligenceThreads.Thread): IntelligenceThread {
  return {
    threadId: row.id,
    title: row.title,
    pinned: row.pinnedAt !== null,
    // `Date.parse` on a server-authored ISO instant. NaN is not defended
    // against with a fallback to `Date.now()`: a thread whose timestamp did not
    // parse would then be filed under "Today", which is a confident answer
    // derived from a failure. It sorts last and groups under "Today" via the
    // clock-skew branch either way — see `dateGroupFor` — and that is a
    // visible oddity rather than a plausible lie.
    updatedAtMs: Date.parse(row.lastActivityAt),
  };
}

/**
 * The durable thread client, always.
 *
 * NOT gated on `intelligence:use` here. Every route behind it re-derives that
 * from the session and answers 403 on its own, the Intelligence route is
 * already behind `RequirePermission`, and a second gate in the client would be
 * a second place for the answer to be wrong. A 403 that does arrive reaches
 * `HistoryPane`'s own failure branch, which says the list could not be loaded —
 * true, and not a claim about why.
 *
 * `undefined` is still the port's type, and the pane still renders its honest
 * not-available note for it. Nothing returns it today; the branch is kept
 * because a deployment that switches the domain off is a state this pane
 * already knows how to say, and deleting the branch would mean re-deriving it
 * from scratch the first time one does.
 */
export function useThreadsClient(): ThreadsClient | undefined {
  return {
    list: () =>
      intelligenceThreads
        .list({ limit: 50 })
        .then((response) => ({ threads: response.threads.map(toThread) })),

    create: (input) =>
      intelligenceThreads.create({ firstMessage: input.firstMessage }).then(toThread),

    messages: (input) =>
      intelligenceThreads.detail(input.threadId, { limit: 200 }).then((response) => ({
        // The wire carries `evidence`, `visualization` and two proposal refs on
        // assistant rows. They are dropped here rather than plumbed through
        // unread: see `ThreadMessage` in `threads.ts` for why a replayed
        // transcript does not reconstruct an interactive answer.
        messages: response.messages.map((message) => ({
          id: message.id,
          role: message.role,
          body: message.body,
        })),
      })),

    rename: (input) =>
      intelligenceThreads.update(input.threadId, { title: input.title }).then(toThread),

    setPinned: (input) =>
      intelligenceThreads.update(input.threadId, { pinned: input.pinned }).then(toThread),

    // Archive is the reversible "get it out of my way" — `archived: true`, not
    // the delete below. The pane offers both and they are not the same act.
    archive: (input) =>
      intelligenceThreads.update(input.threadId, { archived: true }).then(() => undefined),

    // A HARD delete: the thread and every message in it, with nothing to undo.
    // `remove` sends `confirm=true` itself, so a mistyped URL cannot perform
    // one — but the pane still asks the person first, because nothing
    // downstream will.
    remove: (input) => intelligenceThreads.remove(input.threadId).then(() => undefined),
  };
}
